import React, { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  TextField,
  IconButton,
  Typography,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";
import { convertCamelToDisplayName } from "../utils/utils";
import AddIcon from "@mui/icons-material/Add";

// Function to get the display label for an array item
const getArrayItemLabel = (item, index) => {
  if (typeof item === "object" && item !== null && !Array.isArray(item)) {
    // For objects, use the value of the first key
    const firstKey = Object.keys(item)[0];
    return item[firstKey] || `Item ${index + 1}`;
  } else if (Array.isArray(item) && item.length > 0) {
    // For nested arrays, use the first item
    return item[0] || `Item ${index + 1}`;
  } else if (
    typeof item === "string" ||
    typeof item === "number" ||
    typeof item === "boolean"
  ) {
    // For primitive values, use the value itself
    return String(item);
  }
  return `Item ${index + 1}`; // Fallback to default
};

const SortableAccordion = ({
  accordionKey,
  index,
  path,
  item,
  handleWidgetFormChange,
  handleRemoveArrayItem,
  expandedAccordions,
  handleAccordionChange,
  renderWidgetFormFields,
  handleRemoveKey,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: accordionKey });
  const [newKey, setNewKey] = useState("");

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: "#1a1c38",
    color: "#fff",
    marginBottom: "10px",
    border: "1px solid #e5e7eb",
  };

  const handleAddKey = () => {
    if (!newKey || newKey.trim() === "") {
      alert("Please enter a valid key name.");
      return;
    }
    if (
      typeof item === "object" &&
      item !== null &&
      !Array.isArray(item) &&
      Object.keys(item).includes(newKey)
    ) {
      alert("Key already exists.");
      return;
    }
    handleWidgetFormChange([...path, index, newKey], "");
    setNewKey("");
  };

  return (
    <Accordion
      expanded={expandedAccordions[accordionKey] || false}
      onChange={handleAccordionChange(path, index)}
      sx={style}
      ref={setNodeRef}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
        sx={{
          "& .MuiAccordionSummary-content": {
            display: "flex",
            alignItems: "center",
            gap: "10px",
          },
        }}
        onClick={(e) => {
          e.stopPropagation(); // Prevent drag-and-drop listeners from interfering
          handleAccordionChange(path, index)(
            e,
            !expandedAccordions[accordionKey]
          );
        }}
      >
        <DragIndicatorIcon
          {...attributes}
          {...listeners}
          sx={{ color: "#fff", cursor: "grab" }}
        />
        <Typography>
          {convertCamelToDisplayName(getArrayItemLabel(item, index))}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            marginBottom: "10px",
          }}
        >
          <Box sx={{ flex: 1 }}>
            {typeof item === "object" && item !== null ? (
              renderWidgetFormFields(item, [...path, index], handleRemoveKey)
            ) : (
              <TextField
                fullWidth
                label={`Item ${index + 1}`}
                value={item ?? ""}
                type={typeof item === "number" ? "number" : "text"}
                onChange={(e) =>
                  handleWidgetFormChange([...path, index], e.target.value)
                }
                sx={{
                  "& .MuiInputBase-input": { color: "#fff" },
                  "& .MuiInputLabel-root": { color: "#fff" },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e5e7eb",
                  },
                }}
              />
            )}
          </Box>
          {typeof item === "object" &&
            item !== null &&
            !Array.isArray(item) && (
              <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <TextField
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  placeholder="Enter new key"
                  sx={{
                    "& .MuiInputBase-input": { color: "#fff" },
                    "& .MuiInputLabel-root": { color: "#fff" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e5e7eb",
                    },
                  }}
                />
                <Button
                  onClick={handleAddKey}
                  startIcon={<AddIcon />}
                  sx={{
                    color: "#fff",
                    borderColor: "#e5e7eb",
                    "&:hover": { borderColor: "#3b82f6" },
                  }}
                  variant="outlined"
                >
                  Add Key
                </Button>
              </Box>
            )}
          <IconButton
            onClick={() => handleRemoveArrayItem(path, index)}
            sx={{ color: "#fff", alignSelf: "flex-start" }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default SortableAccordion;
